import { Component, Inject, Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { I18nService } from './i18n/i18n.service';
import { InstallAppService } from './install-app';
import { PLAY_STORE_URL, isAndroidBrowser } from './app-store-links';

const DAY_MS = 24 * 60 * 60 * 1000;

/** When the shopper last closed the banner, or went to the store from it or the popup (epoch ms). */
const BANNER_DISMISS_KEY = 'orderyaa.appbanner.dismissedAt';

/** How long a closed banner stays away: long enough not to nag, short enough to ask again. */
const BANNER_QUIET_MS = 14 * DAY_MS;

/** Until when the "download our app" popup stays away (epoch ms). */
const MODAL_QUIET_UNTIL_KEY = 'orderyaa.appmodal.quietUntil';

/** A popup closed with ✕ or the backdrop asks again in a week; one that sent them to the store, in a month. */
const MODAL_CLOSED_MS = 7 * DAY_MS;
const MODAL_WENT_MS = 30 * DAY_MS;

/**
 * What the shopper has said to the "get the app" offers — the Android strip and the popup — kept in one
 * place so going to the Play Store from either one quiets both. Storage can throw in a private window;
 * every read then falls back to "not dismissed" and every write is skipped.
 */
@Injectable({ providedIn: 'root' })
export class GetAppStateService {
  private readonly isBrowser: boolean;

  /** The Android strip: an Android browser that has not closed it recently. Never true on the server. */
  readonly bannerWanted = signal(false);

  /**
   * Until when the popup stays away. A SIGNAL, not just a stored number, so that quieting it from the
   * strip reaches a popup already counting down on this page — see [modalResting].
   */
  private readonly quietUntil = signal(0);

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (!this.isBrowser) return;
    this.quietUntil.set(this.readNumber(MODAL_QUIET_UNTIL_KEY));
    if (!PLAY_STORE_URL || !isAndroidBrowser()) return;
    this.bannerWanted.set(Date.now() - this.readNumber(BANNER_DISMISS_KEY) > BANNER_QUIET_MS);
  }

  closeBanner(): void {
    this.bannerWanted.set(false);
    this.write(BANNER_DISMISS_KEY, Date.now());
    // Closing the strip is a refusal of the app, not of one particular way of being asked — so it
    // buys the same week from the popup that the popup's own ✕ buys. Without this, putting the
    // strip away was answered four seconds later by a full-screen sheet offering the same thing.
    this.rest(MODAL_CLOSED_MS);
  }

  /**
   * The popup was closed, or asked recently enough that it should wait. Reactive: a popup waiting out
   * its delay when the shopper taps Install on the strip is called off rather than landing on top of
   * the store they have just gone to.
   */
  modalResting(): boolean {
    return Date.now() < this.quietUntil();
  }

  closeModal(): void {
    this.rest(MODAL_CLOSED_MS);
  }

  /** The shopper went to the Play Store, from the strip or the popup: neither asks again for a while. */
  wentToStore(): void {
    this.closeBanner();
    this.rest(MODAL_WENT_MS);
  }

  /** Push the popup's deadline out by [ms] — never in, so a close cannot cut short the longer quiet
   *  a trip to the store earned. */
  private rest(ms: number): void {
    // Storage as well as our own signal: a second tab may have earned the longer quiet since this
    // one read it, and closing the popup here must not undo that.
    const until = Math.max(this.quietUntil(), this.readNumber(MODAL_QUIET_UNTIL_KEY), Date.now() + ms);
    this.quietUntil.set(until);
    this.write(MODAL_QUIET_UNTIL_KEY, until);
  }

  private readNumber(key: string): number {
    if (!this.isBrowser) return 0;
    try { return Number(localStorage.getItem(key)) || 0; } catch { return 0; }
  }

  private write(key: string, value: number): void {
    if (!this.isBrowser) return;
    try { localStorage.setItem(key, String(value)); } catch { /* private mode */ }
  }
}

/**
 * The "get the app" strip across the top of every page, on an Android browser only — the one place
 * the Play Store link opens straight into an install. iOS has no App Store listing yet, so an iPhone
 * keeps the home-screen prompt ({@link InstallPromptComponent}) instead; on Android this banner
 * replaces that prompt, so the shopper is never offered two installs at once.
 *
 * Above the sticky header in the page flow, so it scrolls away with the page rather than eating
 * screen height for good. Closing it, or going to the store from it or the popup, keeps it away for
 * two weeks. Never shown inside the installed web app.
 */
@Component({
  selector: 'app-get-app-banner',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (show()) {
      <div class="gab" role="region" [attr.aria-label]="i18n.t('appBanner.aria')">
        <button type="button" class="gab__close" (click)="state.closeBanner()"
                [attr.aria-label]="i18n.t('shops.dismiss')">✕</button>
        <img class="gab__icon" src="images/pwa/icon-192.png" width="44" height="44" alt="" />
        <div class="gab__text">
          <strong class="gab__title">{{ i18n.t('appBanner.title') }}</strong>
          <span class="gab__sub">{{ i18n.t('appBanner.sub') }}</span>
          <span class="gab__store">{{ i18n.t('appBanner.store') }}</span>
        </div>
        <a class="gab__btn" [href]="playStoreUrl" target="_blank" rel="noopener" (click)="state.wentToStore()">
          {{ i18n.t('appBanner.install') }}
        </a>
      </div>
    }
  `,
  styles: [`
    .gab {
      display: flex; align-items: center; gap: 0.6rem;
      padding: 0.55rem 0.75rem 0.55rem 0.4rem;
      background: #fff; border-bottom: 1px solid #f1e7e7;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }
    .gab__close {
      flex: 0 0 auto; border: none; background: transparent; cursor: pointer;
      color: var(--color-text-muted, #5e6e8c); font-size: 0.95rem; line-height: 1; padding: 0.4rem;
    }
    .gab__icon { flex: 0 0 auto; width: 44px; height: 44px; border-radius: 10px; }
    .gab__text { display: flex; flex-direction: column; min-width: 0; flex: 1 1 auto; line-height: 1.25; }
    .gab__title { font-size: 0.9rem; font-weight: 800; color: var(--color-text, #17120f); }
    .gab__sub, .gab__store {
      font-size: 0.75rem; color: var(--color-text-muted, #5e6e8c);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .gab__store { font-weight: 700; }
    .gab__btn {
      flex: 0 0 auto; text-decoration: none; font-weight: 800; font-size: 0.85rem;
      background: var(--color-primary, #FA0E0E); color: #fff;
      padding: 0.5rem 1.05rem; border-radius: 999px;
    }
  `],
})
export class GetAppBannerComponent {
  protected readonly i18n = inject(I18nService);
  private readonly install = inject(InstallAppService);
  protected readonly state = inject(GetAppStateService);
  protected readonly playStoreUrl = PLAY_STORE_URL;

  protected readonly show = computed(() => this.state.bannerWanted() && !this.install.installed());
}

/** Set once the shopper closes the card — on either page, since both show the same one. */
const CARD_DISMISS_KEY = 'orderyaa.apppromo.dismissed';

/**
 * The "scan to get the app" card at the top of the filters column, on both the discover page and the
 * stores list: a QR for a desktop visitor to scan with their phone, and the store badges.
 *
 * The QR is a static asset encoding {@link getAppUrl}; that page detects the device and forwards to the
 * right store, so one code serves Android, iOS and desktop. A pre-rendered asset rather than a
 * third-party generator: no external request on page load, nothing to break if that service goes away,
 * and the shopper's destination URL isn't handed to another host.
 */
@Component({
  selector: 'app-get-app-card',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (open()) {
      <div class="app-promo">
        <button type="button" class="app-promo__close" (click)="dismiss()" [attr.aria-label]="i18n.t('shops.dismiss')">×</button>
        <a class="app-promo__qr-link" [href]="getAppUrl" target="_blank" rel="noopener" [attr.aria-label]="i18n.t('shops.getApp')">
          <img class="app-promo__qr" src="images/common/orderyaa-get-app-qr.svg"
               alt="QR code — scan to get the orderYaa app" width="120" height="120" loading="lazy" />
        </a>
        <p class="app-promo__text">{{ i18n.t('shops.scanForApp') }}<strong> — order from your phone.</strong></p>
        <div class="app-promo__badges">
          <a class="app-promo__badge" [href]="playStoreUrl" target="_blank" rel="noopener">
            <span aria-hidden="true">▶</span> Google Play
          </a>
          <span class="app-promo__badge is-soon" [title]="i18n.t('shops.notPublished')">
            <span aria-hidden="true"></span> App Store — soon
          </span>
        </div>
      </div>
    }
  `,
  styles: [`
    .app-promo {
      position: relative;
      display: flex; flex-direction: column; align-items: center; text-align: center;
      gap: 0.6rem; margin-bottom: 1rem; padding: 1rem 0.9rem 0.9rem;
      background: #fff; border: 1px solid #efe6e6; border-radius: 14px;
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.05);
    }
    .app-promo__close {
      position: absolute; top: 6px; inset-inline-end: 8px; width: 24px; height: 24px;
      border: none; background: transparent; color: #b3a9a9; font-size: 18px; line-height: 1;
      cursor: pointer; border-radius: 6px;
    }
    .app-promo__close:hover { color: #6b6b6b; }
    .app-promo__qr {
      width: 120px; height: 120px; border-radius: 10px;
      border: 1px solid #f0eaea; padding: 6px; background: #fff;
    }
    .app-promo__text { margin: 0; font-size: 0.82rem; line-height: 1.4; color: var(--color-text-muted, #6b6b6b); }
    .app-promo__text strong { color: var(--color-text, #17120f); font-weight: 700; }
    .app-promo__badges { display: flex; flex-direction: column; gap: 0.4rem; width: 100%; }
    .app-promo__badge {
      display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem;
      padding: 0.4rem 0.6rem; border-radius: 8px;
      background: #17120f; color: #fff; font-size: 0.8rem; font-weight: 600;
      opacity: 0.85; cursor: default;
    }
    .app-promo__badge { text-decoration: none; }
    .app-promo__badge:not(.is-soon):hover { border-color: var(--color-primary); color: var(--color-primary); }
    /* Unpublished store: visibly inert, so nobody taps expecting a listing. */
    .app-promo__badge.is-soon { opacity: 0.55; cursor: default; }
    .app-promo__qr-link { display: block; line-height: 0; }

    /* A phone cannot scan its own screen. Below 760px both pages fold this column into a panel the
       shopper opens from "Filters" — a QR at the top of it would be the first thing they met, and
       the one thing there they could not use. The strip and the popup are the phone's offer. */
    @media (max-width: 760px) { .app-promo { display: none; } }
  `],
})
export class GetAppCardComponent {
  protected readonly i18n = inject(I18nService);
  protected readonly getAppUrl = 'https://www.orderyaa.com/get-app';
  protected readonly playStoreUrl = PLAY_STORE_URL;
  /** Closed until storage says otherwise, so the server render and a dismissed visitor never flash it. */
  protected readonly open = signal(false);

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    if (!isPlatformBrowser(platformId)) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(CARD_DISMISS_KEY) === '1'; } catch { /* ignore */ }
    this.open.set(!dismissed);
  }

  protected dismiss(): void {
    this.open.set(false);
    try { localStorage.setItem(CARD_DISMISS_KEY, '1'); } catch { /* ignore */ }
  }
}
